import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "../../layout/AppLayout";
import { Alert, ErpTable, Modal } from "../../components/erp";
import { errorText } from "../../api/client";
import { stationsApi, StationRow, varietiesApi, VarietyRow } from "./api";

const AddModal: React.FC<{ title: string; fields: { key: string; label: string }[]; onClose: () => void;
  onSubmit: (v: Record<string, string>) => Promise<{ Error: string | null }>; testId: string }> = ({ title, fields, onClose, onSubmit, testId }) => {
  const [v, setV] = useState<Record<string, string>>({}); const [err, setErr] = useState<string | null>(null); const [saving, setSaving] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setErr(null);
    try { const r = await onSubmit(v); if (r.Error) setErr(r.Error); else onClose(); }
    catch (ex) { setErr(errorText(ex)); } finally { setSaving(false); }
  };
  return (
    <Modal open title={title} onClose={onClose} testId={testId}>
      <form onSubmit={submit} data-testid={`${testId}-form`}>
        {fields.map((f) => (
          <div className="mb-3" key={f.key}><label className="form-label">{f.label}</label>
            <input className="form-control" value={v[f.key] || ""} onChange={(e) => setV((s) => ({ ...s, [f.key]: e.target.value }))} data-testid={`${testId}-${f.key}`} /></div>
        ))}
        {err && <Alert kind="danger" testId={`${testId}-error`}>{err}</Alert>}
        <button className="btn btn-primary" type="submit" disabled={saving} data-testid={`${testId}-submit`}>{saving ? "Saving..." : "Save"}</button>
      </form>
    </Modal>
  );
};

/** Port of ApiV2/PreProcessing/Controller/PpcStationsListController.php + CreatePpcStationController.php — PpcVAStations. */
export function PpcStationsMaster() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["pp", "stations"], queryFn: () => stationsApi.list() });
  const [open, setOpen] = useState(false);
  return (
    <>
      <PageHeader title="Plant / Center Stations" location="Preprocessing" />
      <div className="card"><div className="card-body">
        <div className="text-center mb-3"><button type="button" className="btn btn-success" onClick={() => setOpen(true)} data-testid="btn-new-station"><i className="fa fa-plus" /> New Station</button></div>
        <ErpTable<StationRow> rows={list.data?.Data || []} loading={list.isLoading} error={list.isError ? errorText(list.error) : list.data?.Error || null} testId="stations-table"
          columns={[{ key: "SNo", title: "S.No" }, { key: "UnitName", title: "Unit Name" }, { key: "UnitLocation", title: "Location" },
            { key: "UnitStatus", title: "Status", render: (r) => r.UnitStatus ? "Active" : "Inactive" }]} />
      </div></div>
      {open && <AddModal title="New Station" testId="station-modal" onClose={() => { setOpen(false); qc.invalidateQueries({ queryKey: ["pp", "stations"] }); }}
        fields={[{ key: "UnitName", label: "Unit Name" }, { key: "UnitLocation", label: "Location" }]}
        onSubmit={(v) => stationsApi.post({ ...v, UnitStatus: 1 })} />}
    </>
  );
}

/** Port of ApiV2/PreProcessing/Controller/HLVAVarietyListController.php + CreateHLVAVarietyController.php — PpcHLVAVarietyList. */
export function HlVaVarietyMaster() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["pp", "varieties"], queryFn: () => varietiesApi.list() });
  const [open, setOpen] = useState(false);
  return (
    <>
      <PageHeader title="HL / VA Variety" location="Preprocessing" />
      <div className="card"><div className="card-body">
        <div className="text-center mb-3"><button type="button" className="btn btn-success" onClick={() => setOpen(true)} data-testid="btn-new-variety"><i className="fa fa-plus" /> New Variety</button></div>
        <ErpTable<VarietyRow> rows={list.data?.Data || []} loading={list.isLoading} error={list.isError ? errorText(list.error) : list.data?.Error || null} testId="varieties-table"
          columns={[{ key: "SNo", title: "S.No" }, { key: "ShortName", title: "Short Name" }, { key: "TypeProduct", title: "Type Product" },
            { key: "FullName", title: "Full Name" }, { key: "Category", title: "Category" }]} />
      </div></div>
      {open && <AddModal title="New Variety" testId="variety-modal" onClose={() => { setOpen(false); qc.invalidateQueries({ queryKey: ["pp", "varieties"] }); }}
        fields={[{ key: "ShortName", label: "Short Name" }, { key: "TypeProduct", label: "Type Product" }, { key: "FullName", label: "Full Name" }, { key: "Category", label: "Category" }]}
        onSubmit={(v) => varietiesApi.post({ ...v, Status: 1 })} />}
    </>
  );
}
